import { useState } from 'react';

import { sameText } from '@/shared/lib/search';

import { Input, Select } from './Field';

/** Значение пункта «Ввести новый…» — такого текста у настоящего варианта не будет. */
const NEW = '\u0000new';

interface SelectOrCreateProps {
  label: string;
  value: string;
  /** То, что уже вводили, — варианты списка. */
  options: readonly string[];
  /** Первый пункт — «не указано»: «Не указан». */
  emptyLabel: string;
  /** Последний пункт, открывающий поле ввода: «Ввести новый…». */
  newLabel: string;
  /** Подпись поля ввода для экранного чтеца: «Новый магазин». */
  newInputLabel: string;
  newPlaceholder?: string;
  onChange: (value: string) => void;
}

/**
 * Выбор из того, что уже вводили, — или новое значение.
 *
 * Выпадающий список, а не ряд кнопок: кнопки с десятком магазинов занимали
 * полкарточки. Нового значения в списке нет — для него последний пункт
 * «Ввести новый…», и под списком появляется поле ввода. Значение, которого нет
 * среди вариантов, — тоже новое: список стоит на «Ввести новый…», а само
 * значение видно в поле. Регистр новым значение не делает: «дикси» — это
 * «Дикси» из списка.
 */
export function SelectOrCreate({
  label,
  value,
  options,
  emptyLabel,
  newLabel,
  newInputLabel,
  newPlaceholder,
  onChange,
}: SelectOrCreateProps) {
  const known = options.find((option) => sameText(option, value));
  // «Ввести новый…» выбран руками — поле остаётся, пока в нём пусто.
  const [chosenNew, setChosenNew] = useState(false);
  const creating = chosenNew || (value.trim() !== '' && known === undefined);

  return (
    <div className="space-y-2">
      <Select
        label={label}
        value={creating ? NEW : (known ?? '')}
        onChange={(event) => {
          const next = event.target.value;
          setChosenNew(next === NEW);
          onChange(next === NEW ? '' : next);
        }}
        options={[
          { value: '', label: emptyLabel },
          ...options.map((option) => ({ value: option, label: option })),
          { value: NEW, label: newLabel },
        ]}
      />
      {creating && (
        <Input
          aria-label={newInputLabel}
          placeholder={newPlaceholder}
          value={value}
          // Поле появилось, потому что его попросили: печатать можно сразу.
          autoFocus={chosenNew}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </div>
  );
}
